'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Breadcumbs from '@/components/Breadcumbs';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { postToApi } from '@/lib/api';

export default function ContactDetails() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = 'Valid email is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.phone.trim() || !/^\d{10}$/.test(formData.phone))
      newErrors.phone = 'Valid 10-digit number is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      FullName: formData.name,
      EmailID: formData.email,
      Subject: formData.subject,
      MobileNo: formData.phone,
      Message: formData.message.trim() || null
    };

    try {
      const response = await postToApi('CreateContactUs', payload);
      if (response.success) {
        router.push('/thank-you');
      } else {
        alert('Something went wrong!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Server error occurred');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    let error = '';

    if (name === 'name' && value.trim() === '') {
      error = 'Name is required';
    }

    if (name === 'subject' && value.trim() === '') {
      error = 'Subject is required';
    }

    if (name === 'message' && value.trim() === '') {
      error = 'Message is required';
    }

    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        error = 'Email is required';
      } else if (!emailRegex.test(value)) {
        error = 'Valid email is required';
      }
    }

    if (name === 'phone') {
      const phoneRegex = /^\d{10}$/;
      if (!value.trim()) {
        error = 'Phone number is required';
      } else if (!phoneRegex.test(value)) {
        error = 'Please enter a valid 10-digit number';
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  return (
    <>
      <Breadcumbs
        title="Contact"
        items={[{ label: 'Home', href: '/' }, { label: 'Contact' }]}
      />
      <div className="contact-us-section css-padding container">
        <div className="row g-5">
          <div className="col-md-7">
            <div className='title-section css-contact-title-mb'>
              <p className='css-heading'>
                <span className='first-title'>Interested in</span>{' '}
                <span className='second-title'>discussing?</span>
              </p>
              <p className='contact-heading'>Active & ready-to-use contact form!</p>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="row">
              {[
                { name: 'name', label: 'Name' },
                { name: 'email', label: 'Email' },
                { name: 'subject', label: 'Subject' },
                { name: 'phone', label: 'Phone' },
              ].map((field) => (
                <div className="col-12 col-lg-6 mb-3" key={field.name}>
                  <label htmlFor={field.name} className="form-label">
                    {field.label} <span className="text-danger">*</span>
                  </label>
                  <input
                    type={
                      field.name === 'email'
                        ? 'email'
                        : field.name === 'phone'
                          ? 'tel'
                          : 'text'
                    }
                    id={field.name}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={`Enter ${field.label}`}
                  />
                  {errors[field.name] && (
                    <p className="text-danger mb-0">{errors[field.name]}</p>
                  )}
                </div>
              ))}

              <div className="col-12 mb-3">
                <label htmlFor="message" className="form-label">
                  Message <span className="text-danger">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter Message"
                  rows="4"
                ></textarea>
                {errors.message && (
                  <p className="text-danger mb-0">{errors.message}</p>
                )}
              </div>

              <div className="col-12 text-center">
                <button type="submit" className="btn text-white submit-css me-2">
                  Submit
                </button>
                <button
                  type="button"
                  className="btn reset-css text-white"
                  onClick={() => {
                    setFormData({
                      name: '',
                      email: '',
                      subject: '',
                      phone: '',
                      message: ''
                    });
                    setErrors({});
                  }}
                >
                  RESET
                </button>
              </div>
            </form>
          </div>

          {/* Right Side Info */}
          <div className="col-md-5">
            <div className='title-section'>
              <h2 className='css-heading'>
                <span className='first-title'>Get in touch </span>
                <span className='second-title'> with us</span>
              </h2>
            </div>
            <div className='css-contact-detail-section'>
              <div className="d-flex align-items-start mb-3">
                <FaPhoneAlt className='css-contact-icon' />
                <div className='css-contact-detail-right'>
                  <p><strong>Phone</strong></p>
                  <p>
                    <Link href="tel:+918544015151">+91 8544015151</Link>,
                    <Link href="tel:+918544025151">+91 8544025151</Link>
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-start mb-3">
                <FaEnvelope className='css-contact-icon' />
                <div className='css-contact-detail-right'>
                  <p><strong>Email</strong></p>
                  <p><Link href="mailto:info@aarogyamhdna.com">info@aarogyamhdna.com</Link></p>
                </div>
              </div>
              <div className="d-flex align-items-start">
                <FaMapMarkerAlt className='css-contact-icon' />
                <div className='css-contact-detail-right'>
                  <p><strong>Address</strong></p>
                  <p>
                    <Link href="https://maps.app.goo.gl/RBbkuC7gq6wmqNor7">
                      Off Bailey Road, Behind Damro Showroom & Maruti Alankar Showroom, Rupaspur, IAS Colony, Patna, Bihar 801503
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Google Map */}
      <div className="iframe-css">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3597.9028883498536!2d85.0576479759396!3d25.608143014932242!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ed57853e9c1c55%3A0x9e0ce5a2f7369010!2sHDNA%20Aarogyam%20Hospital!5e0!3m2!1sen!2sin!4v1751695088372!5m2!1sen!2sin"
          allowFullScreen=""
          loading="lazy"
          title="Aarogyam Hospital Location"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </>
  );
}
